/**
 * The channel address.
 *
 * A channel is addressed on the wire by its composite id — `<channelTypeId>:<entityId>`
 * — and both halves are client-derivable, so a surface that knows "the internal-notes
 * thread of order X" never needs a lookup round-trip to open it.
 *
 * `entityId` is a 24-char nanoid and `channelTypeId` is a snake_case slug, so the
 * separator is the FIRST colon; splitting on every colon would corrupt any future
 * entity id that contains one.
 */

/** The homeowner-facing thread on an order. */
export const CHANNEL_TYPE_ORDER_MESSAGE = "order_message";

/** The team-only internal-notes thread on an order. */
export const CHANNEL_TYPE_ORDER_COMMENT = "order_comment";

/** The household's conversation with Birdwatch Support, addressed by `Place.id`. */
export const CHANNEL_TYPE_SUPPORT_TICKET = "support_ticket";

export type ParsedChannelCid = {
  channelTypeId: string;
  entityId: string;
};

export function buildChannelCid(
  channelTypeId: string,
  entityId: string,
): string {
  return `${channelTypeId}:${entityId}`;
}

/** Returns `null` for anything that is not `<type>:<id>` with both halves non-empty. */
export function parseChannelCid(cid: string): ParsedChannelCid | null {
  const separator = cid.indexOf(":");
  if (separator <= 0 || separator === cid.length - 1) return null;

  return {
    channelTypeId: cid.slice(0, separator),
    entityId: cid.slice(separator + 1),
  };
}

/**
 * The entity kind an order channel hangs off — platform's `CHANNEL_ENTITY.ORDER`
 * (`platform/src/lib/channels/channel-cid.ts`).
 */
export const CHANNEL_ENTITY_ORDER = "order";

/**
 * The order a channel belongs to, or `""` when it is not an order channel (a home's
 * support conversation).
 *
 * One home for the question. Every order-domain block renderer needs it, and before this
 * each answered it differently — the office half read the router (`useOrderId()`, which is
 * `undefined` anywhere but an order route) and the member half had it threaded in as a
 * prop. The channel row is on hand wherever a block renders, so the answer travels with
 * the block rather than with the screen.
 */
export function orderIdOfChannel(
  channel: { entityType: string; entityId: string } | undefined,
): string {
  if (channel?.entityType !== CHANNEL_ENTITY_ORDER) return "";
  return channel.entityId;
}

/** The two channel types that hang off an order: its homeowner thread and its notes. */
const ORDER_CHANNEL_TYPES: ReadonlySet<string> = new Set([
  CHANNEL_TYPE_ORDER_MESSAGE,
  CHANNEL_TYPE_ORDER_COMMENT,
]);

/**
 * The order a cid addresses, or `null` when it is not an order thread — for a writer
 * that holds only the cid, not the channel row `orderIdOfChannel` reads.
 */
export function orderIdOfCid(cid: string): string | null {
  const parsed = parseChannelCid(cid);
  if (!parsed || !ORDER_CHANNEL_TYPES.has(parsed.channelTypeId)) return null;
  return parsed.entityId;
}
